import React from "react";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import PersonIcon from "@mui/icons-material/Person";
import baseUrl from "../../../../api/baseUrl";
import socket from "../../../../api/socket";
import {
  APPOINTMENT_STATUS_OPTIONS,
  bookingSourceStyles,
  doctorNameFromItem,
  FIELD_LABELS,
  formatAppointmentPurpose,
  formatPreAppointmentStatus,
  getLocationFieldLabel,
  isAppointmentCancelled,
  isRoomAllocationEnabled,
  latestTransferDoctorNameFromItem,
  normalizeLifecycleStatus,
  patientMobileFromItem,
  patientRegNoFromItem,
  preAppointmentStatusFromItem,
  transferDoctorNamesFromItem,
  statusStyles,
} from "./appointmentViewShared";
import { isDoctorTransferEnabled, isTreatmentsEnabled } from "../../../../utils/clinicFeatures";
import { toast } from "react-toastify";
import PatientViewModal from "../../../patient/components/PatientViewModal";
import { openPatientSummaryFromAppointment, tokenInfoFromAppointment } from "../../../../utils/openPatientSummaryInNewTab";
import { formatTokenTime } from "../../../patient/components/ModalHeader";
import { useOngoingStatusGate } from "./useOngoingStatusGate";
import UpdateTokenModal from "./UpdateTokenModal";
import UnverifiedBookingBadge from "./UnverifiedBookingBadge";
import { patientNameFromAppointment } from "../../../../utils/dispatchTokenPrint";

type AppointmentMobileCardProps = {
  item: Record<string, any>;
  onOpenDetails?: () => void;
  onStatusChange: () => void;
  onAddTreatment?: (patientId: string, appointmentId?: string) => void;
  onAddDiagnosis?: (patientId: string, appointmentId?: string) => void;
  showAddTreatmentButton?: boolean;
  showPatientDetailsButton?: boolean;
  showViewAppointmentButton?: boolean;
  onOngoingStatus?: (patientId: string) => void;
  // Allow extra props without TS errors (some pages pass feature flags).
  [key: string]: any;
};

function FieldBlock({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide block mb-1">
        {label}
      </span>
      {children}
    </div>
  );
}

export default function AppointmentMobileCard({
  item,
  onOpenDetails,
  onStatusChange,
  onAddTreatment: onAddTreatmentProp,
  onAddDiagnosis: onAddDiagnosisProp,
  showAddTreatmentButton = true,
  showPatientDetailsButton = false,
  showViewAppointmentButton = true,
  showDoctorColumn = true,
  tokenOnly = false,
  hideCancelAction = false,
  onOngoingStatus,
}: AppointmentMobileCardProps) {
  const roomAllocationEnabled = isRoomAllocationEnabled();
  // No treatment actions for BookMyDentist only clinics
  const treatmentsEnabled = isTreatmentsEnabled();
  const onAddTreatment = treatmentsEnabled ? onAddTreatmentProp : undefined;
  const onAddDiagnosis = treatmentsEnabled ? onAddDiagnosisProp : undefined;
  const doctorTransferEnabled = isDoctorTransferEnabled();
  const patientName =
    item?.patientId?.fullName || item?.bookPatientId?.fullName || "N/A";
  const doctorName = doctorNameFromItem(item);
  const transferDoctorName = latestTransferDoctorNameFromItem(item);
  const room = String(item?.roomNumber ?? "").trim() || "—";
  const queue =
    item?.queue != null && item?.queue !== ""
      ? String(Number(item.queue))
      : "—";
  const hasToken = queue !== "—" && Number(queue) > 0;
  const tokenGeneratedTime =
    roomAllocationEnabled && hasToken
      ? formatTokenTime(item?.queueGeneratedAt)
      : "";
  const canUpdateQueue =
    roomAllocationEnabled &&
    String(item?.sessionName ?? "").trim() !== "" &&
    String(item?.roomNumber ?? "").trim() !== "";
  const hasPatient = Boolean(item?.patientId?.fullName && item?.patientId?._id);
  const [status, setStatus] = React.useState(
    () => normalizeLifecycleStatus(item?.status) || item?.status || ""
  );
  const pendingStatusRef = React.useRef<string | null>(null);
  const [patientModalOpen, setPatientModalOpen] = React.useState(false);
  const { gateStatus, ongoingGateModal } = useOngoingStatusGate();
  const [patientLoading, setPatientLoading] = React.useState(false);
  const [patientData, setPatientData] = React.useState<any>(null);
  const [theme, setTheme] = React.useState<"light" | "dark">("light");
  const [tokenModalOpen, setTokenModalOpen] = React.useState(false);
  const [tokenSaving, setTokenSaving] = React.useState(false);

  React.useEffect(() => {
    if (pendingStatusRef.current) return;
    setStatus(normalizeLifecycleStatus(item?.status) || item?.status || "");
  }, [item?.status, item?._id]);

  React.useEffect(() => {
    const htmlTheme = document.documentElement.getAttribute("data-theme") as "light" | "dark";
    const savedTheme = (localStorage.getItem("theme") as "light" | "dark") || htmlTheme;
    if (savedTheme) setTheme(savedTheme);
  }, []);

  const handleViewPatient = async () => {
    const pid = item?.patientId?._id;
    if (!pid) return;
    setPatientModalOpen(true);
    setPatientLoading(true);
    try {
      const res = await baseUrl.get("patient/findById", { headers: { patientid: pid } });
      setPatientData(res.data?.data ?? null);
    } catch (err) {
      console.error(err);
      setPatientData(null);
    } finally {
      setPatientLoading(false);
    }
  };

  const closePatientModal = () => {
    setPatientModalOpen(false);
    setPatientData(null);
  };

  const handleAddTreatmentFromSummary = (
    patientId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    closePatientModal();
    onAddTreatment?.(patientId, item?._id ? String(item._id) : undefined);
  };

  const handleAddDiagnosisFromSummary = (
    patientId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    closePatientModal();
    onAddDiagnosis?.(patientId, item?._id ? String(item._id) : undefined);
  };

  const openUpdateTokenModal = () => {
    if (!canUpdateQueue || !item?._id || tokenSaving) return;
    setTokenModalOpen(true);
  };

  const handleConfirmUpdateToken = async (n: number) => {
    if (!canUpdateQueue || !item?._id || tokenSaving) return;

    setTokenSaving(true);

    try {
      await baseUrl.patch(
        "/appointment/queueAppointmentById",
        { queue: n },
        { headers: { appointmentid: item._id } }
      );

      socket.emit("allAppointmentStatus", {
        appointmentid: item._id,
        status: item?.status,
        queue: n,
        roomNumber: item?.roomNumber,
        sessionName: item?.sessionName,
      });

      setTokenModalOpen(false);
      onStatusChange?.();
    } catch (err) {
      console.error(err);
      toast.error("Could not update token. Please try again.");
    } finally {
      setTokenSaving(false);
    }
  };

  const handleGenerateQueue = async () => {
    if (!canUpdateQueue || !item?._id) return;
    try {
      const res = await baseUrl.patch(
        "/appointment/generateQueueAppointmentById",
        {},
        { headers: { appointmentid: item._id } }
      );
      const q = res?.data?.data?.queue;
      if (q == null) return;

      socket.emit("allAppointmentStatus", {
        appointmentid: item._id,
        status: "inqueue",
        queue: q,
        roomNumber: item?.roomNumber,
        sessionName: item?.sessionName,
      });

      onStatusChange?.();
    } catch (err) {
      console.error(err);
    }
  };

  const handleStatusChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextStatus = e.target.value;
    if (!nextStatus || !item?._id) return;

    const ok = await gateStatus(item, nextStatus);
    if (!ok) {
      e.target.value = status;
      return;
    }

    pendingStatusRef.current = nextStatus;
    setStatus(nextStatus);

    socket.emit("allAppointmentStatus", {
      appointmentid: item._id,
      status: nextStatus,
      queue: item?.queue,
      roomNumber: item?.roomNumber,
      sessionName: item?.sessionName,
    });

    if (nextStatus === "ongoing" && item?.queue != null && item?.queue !== "") {
      try {
        localStorage.setItem(
          "nextQueue",
          String(Math.max(0, Math.floor(Number(item.queue))))
        );
      } catch {
        /* ignore */
      }
    }

    if (nextStatus === "ongoing") {
      openPatientSummaryFromAppointment(item);
    }

    baseUrl
      .patch(
        "/appointment/statusAppointmentById",
        { status: nextStatus },
        { headers: { appointmentid: item._id, status: nextStatus } }
      )
      .then(() => {
        pendingStatusRef.current = null;
        onStatusChange();
      })
      .catch((err) => {
        pendingStatusRef.current = null;
        setStatus(normalizeLifecycleStatus(item?.status) || item?.status || "");
        toast.error(
          err?.response?.data?.error || "Could not update appointment status."
        );
      });
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 border-l-4 border-l-indigo-400 dark:border-l-indigo-500 p-4 space-y-3 shadow-sm hover:shadow-md dark:hover:bg-slate-700/50 transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base text-slate-900 dark:text-slate-100 truncate">
            {patientName}
          </h3>
          {(() => {
            const source = preAppointmentStatusFromItem(item);
            const label = formatPreAppointmentStatus(source);
            if (!label) return null;
            return (
              <span
                className={`mt-1 inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold border ${
                  bookingSourceStyles[source] || bookingSourceStyles.default
                }`}
                title={source === "cancel" ? "Cancelled" : `Booked via ${label}`}
              >
                {source === "cancel" ? "Cancelled" : `Booked · ${label}`}
              </span>
            );
          })()}
          <UnverifiedBookingBadge item={item} className="ml-1 mt-1" />
          <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400 truncate">
            Reg {patientRegNoFromItem(item)}
            {" · "}
            {patientMobileFromItem(item)}
          </p>
          {doctorTransferEnabled && transferDoctorName ? (
            <p className="mt-0.5 text-xs text-sky-700 dark:text-sky-300">
              Transferred to {transferDoctorName}
            </p>
          ) : null}
        </div>
        <div className="flex gap-1.5 flex-shrink-0">
          {showViewAppointmentButton ? (
            <button
              type="button"
              onClick={onOpenDetails}
              className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 flex items-center justify-center hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-colors"
              title="View appointment details"
              aria-label="View appointment details"
            >
              <RemoveRedEyeIcon sx={{ fontSize: 18 }} />
            </button>
          ) : null}
          {hasPatient && (showPatientDetailsButton || !onAddTreatment) ? (
            <button
              type="button"
              onClick={handleViewPatient}
              className="w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-900/40 text-slate-700 dark:text-slate-200 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-900/60 transition-colors"
              title="View patient"
              aria-label="View patient"
            >
              <PersonIcon sx={{ fontSize: 18 }} />
            </button>
          ) : null}
          {showAddTreatmentButton && hasPatient && onAddTreatment ? (
            <button
              type="button"
              onClick={() =>
                onAddTreatment(
                  item.patientId._id,
                  item?._id ? String(item._id) : undefined
                )
              }
              className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 flex items-center justify-center hover:bg-emerald-100 dark:hover:bg-emerald-900/60 transition-colors"
              title="Add Treatment"
              aria-label="Add Treatment"
            >
              <MedicalInformationIcon sx={{ fontSize: 18 }} />
            </button>
          ) : null}
        </div>
      </div>

      {showDoctorColumn ? (
        <FieldBlock label={FIELD_LABELS.doctor}>
          <p className="text-sm text-slate-700 dark:text-slate-200">{doctorName}</p>
        </FieldBlock>
      ) : null}

      <FieldBlock label={FIELD_LABELS.dateTime}>
        <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">
          {item?.date?.split("T")[0] ?? "—"}
        </p>
        <p className="mt-0.5 text-[11px] text-slate-600 dark:text-slate-300">
          {item?.startTime} - {item?.endTime}
        </p>
      </FieldBlock>

      <FieldBlock label={getLocationFieldLabel(tokenOnly)}>
        {tokenOnly ? (
          <>
            <p className="text-3xl font-bold tabular-nums text-slate-900 dark:text-slate-50 leading-none tracking-tight">
              {queue}
            </p>
            {canUpdateQueue ? (
              <p className="mt-1.5 text-xs text-slate-600 dark:text-slate-300">
                <button
                  type="button"
                  onClick={openUpdateTokenModal}
                  className="underline underline-offset-2 text-primary hover:text-primary/80"
                >
                  Update token
                </button>
                <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
                <button
                  type="button"
                  onClick={handleGenerateQueue}
                  className="underline underline-offset-2 text-emerald-700 hover:text-emerald-700/80 dark:text-emerald-300 dark:hover:text-emerald-200"
                >
                  Generate token
                </button>
              </p>
            ) : null}
            {tokenGeneratedTime ? (
              <p
                className="mt-1 text-sm font-medium text-slate-700 dark:text-slate-200"
                title={`Token generated at ${tokenGeneratedTime}`}
              >
                Generated {tokenGeneratedTime}
              </p>
            ) : null}
          </>
        ) : (
          <>
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
              {item?.sessionName || "—"}
            </p>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
              Room {room}
              {roomAllocationEnabled ? (
                <>
                  <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
                  <span className="tabular-nums">Token {queue}</span>
                </>
              ) : null}
              {canUpdateQueue ? (
                <>
                  <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
                  <button
                    type="button"
                    onClick={openUpdateTokenModal}
                    className="underline underline-offset-2 text-primary hover:text-primary/80"
                  >
                    Update token
                  </button>
                  <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
                  <button
                    type="button"
                    onClick={handleGenerateQueue}
                    className="underline underline-offset-2 text-emerald-700 hover:text-emerald-700/80 dark:text-emerald-300 dark:hover:text-emerald-200"
                  >
                    Generate token
                  </button>
                </>
              ) : null}
            </p>
            {tokenGeneratedTime ? (
              <p
                className="mt-1 text-sm font-medium text-slate-700 dark:text-slate-200"
                title={`Token generated at ${tokenGeneratedTime}`}
              >
                Generated {tokenGeneratedTime}
              </p>
            ) : null}
          </>
        )}
      </FieldBlock>

      <div className="grid grid-cols-2 gap-3">
        <FieldBlock label={FIELD_LABELS.status}>
          {isAppointmentCancelled(item) ? (
            <span className="text-sm text-slate-400 dark:text-slate-500">—</span>
          ) : (
            <select
              onChange={handleStatusChange}
              value={normalizeLifecycleStatus(status) || status || ""}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 w-full ${
                statusStyles[normalizeLifecycleStatus(status) || status] ||
                statusStyles.default
              }`}
            >
              <option value="">Select</option>
              {APPOINTMENT_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              {!hideCancelAction ? (
                <option value="cancel">Cancel</option>
              ) : null}
            </select>
          )}
        </FieldBlock>
      </div>

      <FieldBlock label={FIELD_LABELS.purpose}>
        <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">
          {formatAppointmentPurpose(item)}
        </p>
      </FieldBlock>

      <PatientViewModal
        isOpen={patientModalOpen}
        onClose={closePatientModal}
        patientData={patientData}
        loading={patientLoading}
        theme={theme}
        appointmentId={item?._id}
        transferDoctorNames={transferDoctorNamesFromItem(item) || undefined}
        onTransferSuccess={onStatusChange}
        tokenInfo={tokenInfoFromAppointment(item)}
        onAddTreatment={
          onAddTreatment && item?.patientId?._id
            ? handleAddTreatmentFromSummary
            : undefined
        }
        onAddDiagnosis={
          onAddDiagnosis && item?.patientId?._id
            ? handleAddDiagnosisFromSummary
            : undefined
        }
      />
      {ongoingGateModal}
      <UpdateTokenModal
        open={tokenModalOpen}
        currentToken={item?.queue}
        patientName={patientNameFromAppointment(item)}
        saving={tokenSaving}
        onCancel={() => {
          if (!tokenSaving) setTokenModalOpen(false);
        }}
        onConfirm={handleConfirmUpdateToken}
      />
    </div>
  );
}
